import React from "react";
import {
  Box,
  Button,
  Modal,
  SpaceBetween,
  TextContent,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useClaimInvestigation,
} from "../../hooks/useInvestigations";

interface TakeOwnershipModalProps {
  stage: Stage;
  investigation: Investigation;
  visible: boolean;
  onDismiss: () => void;
  onError: (message: string) => void;
}

/**
 * Confirmation for claiming a case. Shared by the ownerless banner at the top of the page and
 * the Owner row in the Overview so both paths behave identically.
 */
export const TakeOwnershipModal: React.FC<TakeOwnershipModalProps> = ({
  stage,
  investigation,
  visible,
  onDismiss,
  onError,
}) => {
  const claim = useClaimInvestigation(stage);

  const doClaim = () =>
    claim
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
      })
      .then(onDismiss)
      .catch((err: InvestigationApiError) => onError(err.message));

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Take ownership?"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                doClaim().catch(console.error);
              }}
              loading={claim.isLoading}
            >
              Take ownership
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <TextContent>
        <p>
          Current owner:{" "}
          <strong>{investigation.owner || "none — needs an owner"}</strong>.
        </p>
        <p>
          You become the owner of this investigation.
          {investigation.owner
            ? ` ${investigation.owner} is notified and stays on the case as a contributor.`
            : ""}{" "}
          The change is recorded in the investigation audit trail.
        </p>
      </TextContent>
    </Modal>
  );
};

export default TakeOwnershipModal;
